/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import type { AnyProject } from '@modules/project-api/types'
import type { ProjectType, ProjectID, ProjectTypeWithWorkplaceAI } from '@modules/ui-types/projects'
import { useProjectCpsContext } from '@modules/project-components/ProjectCps/ProjectCpsContext'

type LinkProjectsParams = {
  projectId: ProjectID
  projectType: ProjectType
  selectedProjects: AnyProject[]
}

export const useLinkProjects = () => {
  const {
    usePatchElasticsearchProjectMutation,
    usePatchObservabilityProjectMutation,
    usePatchSecurityProjectMutation,
  } = useProjectCpsContext()

  const patchElasticsearchMutation = usePatchElasticsearchProjectMutation()
  const patchObservabilityMutation = usePatchObservabilityProjectMutation()
  const patchSecurityMutation = usePatchSecurityProjectMutation()

  const getPatchMutation = (projectType: ProjectType) => {
    switch (projectType) {
      case 'elasticsearch':
        return patchElasticsearchMutation
      case 'observability':
        return patchObservabilityMutation
      case 'security':
        return patchSecurityMutation
      default:
        return patchElasticsearchMutation
    }
  }

  const linkProjects = async ({ projectId, projectType, selectedProjects }: LinkProjectsParams) => {
    const mutation = getPatchMutation(projectType)

    // Build the linked projects structure with only new projects
    const linkedProjects: Record<string, { type: ProjectTypeWithWorkplaceAI }> = {}

    selectedProjects.forEach((selectedProject) => {
      linkedProjects[selectedProject.id] = {
        type: selectedProject.type,
      }
    })

    return mutation.mutateAsync({
      id: projectId,
      body: {
        linked: {
          projects: linkedProjects,
        },
      },
    })
  }

  const resetErrors = () => {
    patchElasticsearchMutation.reset()
    patchObservabilityMutation.reset()
    patchSecurityMutation.reset()
  }

  return {
    linkProjects,
    resetErrors,
    isLoading:
      patchElasticsearchMutation.isLoading ||
      patchObservabilityMutation.isLoading ||
      patchSecurityMutation.isLoading,
    error:
      patchElasticsearchMutation.error ||
      patchObservabilityMutation.error ||
      patchSecurityMutation.error,
  }
}
